// Display formatting for the dashboard, in one place so two widgets never
// disagree about what a number or a signal level looks like.

/** Compact integers: 1,240 stays "1,240"; 18,400 becomes "18.4k". */
export function count(n: number): string {
  const v = Number(n) || 0;
  if (v < 10000) return v.toLocaleString();
  if (v < 1e6) return `${(v / 1000).toFixed(1)}k`;
  return `${(v / 1e6).toFixed(1)}M`;
}

/** The eight categorical slots in fixed order. Past the end is the muted
 * "none" colour rather than a wrap: a ninth series is folded into "Other"
 * by the caller, never silently given slot one's hue. */
export function seriesColor(i: number): string {
  return i >= 0 && i < 8 ? `var(--series-${i + 1})` : "var(--rf-none)";
}

export type RssiBucket = "good" | "fair" | "poor" | "none";

/** The same boundaries the server states in every signal answer; mirrored
 * here only to colour a number the server already classified. */
export const RSSI_GOOD = -65;
export const RSSI_FAIR = -75;

export function rssiBucket(rssi?: number | null): RssiBucket {
  if (rssi === undefined || rssi === null) return "none";
  if (rssi >= RSSI_GOOD) return "good";
  if (rssi >= RSSI_FAIR) return "fair";
  return "poor";
}

/** HH:MM in the viewer's locale, for "updated at". */
export function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

export function plural(n: number, one: string, many = one + "s"): string {
  return n === 1 ? one : many;
}

/** How long before the read the device wrote its report. A report dated
 * after the read is the device's clock running ahead of this browser's, and
 * is said as that rather than as a report from the future.
 *
 * `subject` is the noun the clock belongs to. It defaults to the switch this
 * function was written for and is `access point` on Operate › Wireless
 * (#1165), which reads the same `unit.localtime` from the same shape of
 * report — one implementation rather than two that drift. */
export function reportAge(reportedAt: Date, readAt: Date, subject = "switch"): string {
  const s = Math.round((readAt.getTime() - reportedAt.getTime()) / 1000);
  if (s < 0) return `the ${subject}\u2019s clock is ahead of this browser\u2019s`;
  if (s < 60) return `${s} s before this read`;
  const m = Math.floor(s / 60), r = s % 60;
  return `${m} min${r ? ` ${r} s` : ""} before this read`;
}

/** The same age, said in the largest unit that fits, for a figure a reader
 * judges at a glance rather than reads beside its device.
 *
 * `reportAge` above says minutes however many there are: seven hours is
 * `420 min before this read` and a day is `1440 min`. Beside a serial, in a
 * row that already names one device, that is readable \u2014 the reader is
 * comparing one number with its neighbours. As the **one figure** a page
 * states to say how stale it is (#1270), it is not: `420 min` scans as the
 * same kind of number as `2 min`, and the seven-hour report that
 * `docs/evidence/2026-10-01-operate-wireless-showed-openwifi-on-air-\u2026` was
 * written about is exactly the case the figure exists to make visible.
 *
 * **A sibling rather than a widening**, deliberately: `reportAge` is read by
 * Operate \u203a Switch's port page and by Operate \u203a Wireless's SSID rows, and
 * changing it would change both (zenith#1270's Out keeps SSIDs on air as it
 * is). The two agree below an hour and are tested against each other.
 *
 * The minutes are kept beside the hours \u2014 `7 h 12 min`, not `7 h` \u2014 because
 * an age rounded down to the unit is a smaller number than the one measured,
 * and this figure's whole job is to not understate staleness. */
export function coarseReportAge(reportedAt: Date, readAt: Date, subject = "switch"): string {
  const s = Math.round((readAt.getTime() - reportedAt.getTime()) / 1000);
  if (s < 3600) return reportAge(reportedAt, readAt, subject);
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d} d${h ? ` ${h} h` : ""} before this read`;
  return `${h} h${m ? ` ${m} min` : ""} before this read`;
}

/** A language model's answer as paragraphs: plain text, separated by a blank
 * line — the contract's `Briefing.text` and `Diagnosis.text` alike, so the
 * Overview's briefing and a device page's diagnosis (#1864) split it the same
 * way. Rendered as text, never as markup. It lives here rather than beside
 * either card so a page importing it does not import the other card's request
 * (Operate's write allow-list walks imports, `shell/operate.test.ts`). */
export function paragraphs(text: string): string[] {
  return text.split(/\n\s*\n/).map((p) => p.trim()).filter((p) => p.length > 0);
}
